/**
 * Every write the RFQ Builder makes, now on `quoflow.quoflow.quoflow.api.enquiry.*` — Task 7.
 *
 * ---- what moved, what didn't ----
 *
 * `useCreateEnquiry`, `useUpdateEnquiry` (replaces `useUpdateHeader`) and `useAssignPurchaser` are
 * the three hooks this task's brief names as its "Produces" — rewritten onto `create_enquiry`,
 * `update_enquiry` and `assign_purchaser`.
 *
 * `useUpdateLine`, `useAddLine`, `useDeleteLine`, `useAssignSuppliers` and `useMatchLines` are
 * NOT separate endpoints on the new API — there are none. They are all read-modify-write against
 * `update_enquiry`'s wholesale `items` replace, funnelled through the one shared primitive below
 * (`updateEnquiryItems`), the same shape the old `updateOpportunityItems` had. Their PUBLIC
 * signatures are unchanged on purpose, so `RfqPage.tsx` (out of this task's file list) needed no
 * edits at all.
 *
 * Three hooks are deliberately NOT touched:
 *
 * - `useGenerateRfqs` stays on the old `/api/v1` client. Task 8 replaces it with `rfq.
 *   generate_rfqs` — rewriting it here would collide with that task's ownership of the endpoint.
 * - `useExtractFile` stays on the old `/api/v1` client too, but for a different reason: the
 *   dispatch for this task suggested folding it onto `mail.extract_enquiry_items` (Task 6,
 *   `useMailExtraction.ts`). That doesn't fit — `mail.extract_enquiry_items` takes a
 *   `communication` (an existing mail's id) and `RfqPage.tsx`'s only call site for this hook is
 *   the "No mail? Upload the customer's list" button, a bare `File` with no mail behind it at
 *   all. There is no live method for a mail-less file-upload extraction in this task's five
 *   endpoints (or Task 8's four), so this hook is left exactly as it was — same treatment as
 *   `useGenerateRfqs` — until a real one exists. See task-7-report.md.
 * - `useExtract` (the JSON `{email_id, use_body}` variant, distinct from `useExtractFile`) had NO
 *   call site anywhere in the app — confirmed by grep — and is deleted (task 12) rather than kept
 *   on the old transport for a hook nothing calls.
 *
 * ---- the read-modify-write race ----
 *
 * `updateEnquiryItems` reads the enquiry, applies a change, and writes the WHOLE items array
 * back — a row you don't send back is deleted (see the module's own line in the task dispatch).
 * Two of those in quick succession (two rapid line edits) must not let the SECOND one's read
 * start from the state BEFORE the FIRST one's write landed — that would silently lose the first
 * edit the moment the second write completes. `withEnquiryLock` below serializes every
 * read-modify-write for one enquiry through a promise chain, so the second call's read cannot
 * start until the first call's write (and its own read-modify-write) has actually settled. See
 * `useEnquiries.test.ts`'s dedicated race test.
 */

import { useMutation, useQueryClient, type UseMutationOptions } from '@tanstack/react-query'
import { queryClient } from '../queryClient'
import { api, BosunApiError } from './client'
import { callGet, callPost, callPut } from './bosun'
import { lookupSupplierAddresses } from './supplierContacts'
import { insertLineAt } from '../insertLine'
import { getBaseCurrency } from './baseCurrency'
import { getResourceDoc } from './frappe'
import { createEnquiryFlow } from './createEnquiryFlow'
import { BosunApiError as TransportError } from './errors'
import { FrappeApiError } from './frappe'
import {
  readRfqList,
  toEnquiry,
  toRfqStatus,
  toRoute,
  type WireEnquiryDetail,
  type WireEnquiryItem,
  type WireRfq,
} from './useEnquiries'
import { num, str } from './wire'
import { qk } from './keys'
import { getCompany } from './company'
import { buildRfqItemRows, buildRfqPlan, textToHtml, withoutGreetings, type ExistingRfq } from '../rfq'
import { getResource } from './frappe'
import type {
  AssignSuppliersPayload,
  Enquiry,
  EnquiryItem,
  EnquiryItemPatch,
  EnquiryStatus,
  EnquiryType,
  ExtractResponse,
  MatchLinesPayload,
  NewEnquiryItem,
  Rfq,
  SendRfqPayload,
  SendRfqResult,
} from '../types'

/** A line PATCH may come back with the non-blocking qty-vs-stock warning (contract §6). Nothing
 * on the new wire echoes one back (see `useEnquiries.ts`'s `toEnquiryItem`), so this is always
 * `undefined` in practice now — kept so `RfqPage.tsx`'s `line.warnings?.forEach(...)` stays valid. */
export type PatchedLine = EnquiryItem & { warnings?: string[] }

type Options<TData, TVars> = Omit<
  UseMutationOptions<TData, BosunApiError, TVars>,
  'mutationFn' | 'onSuccess'
> & {
  onSuccess?: (data: TData, vars: TVars) => void
  /**
   * Set by a hook whose write path calls `seedEnquiry` — it suppresses the cache invalidation,
   * because the authoritative document is already in the cache and re-fetching it is a wasted
   * ~0.85s round trip. Internal to this file; never passed by a screen.
   */
  seedsEnquiry?: boolean
}

/**
 * Every error this file can throw — `FrappeApiError` (the old Frappe transport; nothing below
 * calls it any more after this task, but the adapter is harmless to keep general) or the new
 * transport's `BosunApiError` (`./errors`, aliased `TransportError` to avoid colliding with the
 * OLD `./client` `BosunApiError` this file's public signatures are still typed against —
 * `RfqPage.tsx`'s `.onError` handlers read `.body?.available`, a property only the OLD shape has)
 * — converges on the OLD one, unchanged from before this task.
 */
function toBosunApiError(error: unknown): BosunApiError {
  if (error instanceof BosunApiError) return error
  if (error instanceof FrappeApiError) {
    return new BosunApiError(error.status, { code: error.code, message: error.message }, error.message)
  }
  if (error instanceof TransportError) {
    return new BosunApiError(error.status, { code: error.code, message: error.message }, error.message)
  }
  const message = error instanceof Error ? error.message : 'Unknown error'
  return new BosunApiError(500, undefined, message)
}

/** Wraps an async fn so every rejection is a `BosunApiError` — see the module header.
 *  Exported for `useSuppliers.ts`'s `useCreateSupplier`, which is on the new transport too and
 *  needs the same convergence; the adapter above is general, not enquiry-specific. */
export function withBosunError<TArgs extends unknown[], TResult>(
  fn: (...args: TArgs) => Promise<TResult>,
): (...args: TArgs) => Promise<TResult> {
  return async (...args: TArgs) => {
    try {
      return await fn(...args)
    } catch (error) {
      throw toBosunApiError(error)
    }
  }
}

/** Shared wiring: run the call, refresh the enquiry, then hand control back to the caller. */
/**
 * Puts the authoritative post-write document straight into the cache.
 *
 * Called by the two write primitives, which hold it already. Without this, `useEnquiryMutation`
 * invalidates and React Query fetches the identical document again — ~0.85s of pure waiting on
 * every line edit (see `updateEnquiryItems`).
 *
 * Uses the exported client rather than a hook because both primitives are plain async functions,
 * callable outside a component (`RfqPage`'s "accept all" loop drives them directly).
 */
function seedEnquiry(enquiryId: string, enquiry: Enquiry): void {
  queryClient.setQueryData(qk.enquiry(enquiryId), enquiry)
}

function useEnquiryMutation<TData, TVars>(
  enquiryId: string,
  mutationFn: (vars: TVars) => Promise<TData>,
  options?: Options<TData, TVars>,
) {
  const client = useQueryClient()
  const { onSuccess, seedsEnquiry, ...rest } = options ?? {}

  return useMutation<TData, BosunApiError, TVars>({
    mutationFn: withBosunError(mutationFn),
    ...rest,
    onSuccess: (data, vars) => {
      // A mutation that went through `updateEnquiryItems`/`callUpdateEnquiry` has already put the
      // server's own post-write document in the cache; invalidating would throw it away and
      // re-fetch it. One that did NOT (assign_purchaser, the RFQ status move, the old-client
      // upload) still has to ask.
      if (!seedsEnquiry) void client.invalidateQueries({ queryKey: qk.enquiry(enquiryId) })
      onSuccess?.(data, vars)
    },
  })
}

const base = (id: string) => `/enquiries/${encodeURIComponent(id)}`

/* ---------- create ---------- */

/**
 * `enquiry.create_enquiry` — a requirement that arrived by phone or WhatsApp rather than mail
 * (contract §5), or the "No mail? … add items manually" path on the RFQ screen. Returns the new
 * enquiry so the caller can navigate straight to it.
 *
 * `create_enquiry`'s documented body is `{source, enquiry_type, customer, transaction_date,
 * delivery_location, vessel, port_of_delivery, items}` — narrower than the old bespoke
 * `EnquiryHeaderPatch` this hook used to take (no subject/sender/company/department; see
 * `EnquiryHeaderCard.tsx`'s own header for the read-side half of this same gap). `sales_person`
 * in particular has NO field here and no other endpoint either — a sales-created enquiry cannot
 * be assigned to its creator at create time, unlike a purchaser-created one (which this hook
 * follows up with `assign_purchaser`, a real endpoint). Flagged in task-7-report.md.
 *
 * The response shape for `create_enquiry` is NOT in the verified shapes this task was given —
 * only its request body is. Rather than trust an unverified response shape, this always follows
 * up with a `get_enquiry` read once the id is known, exactly like every other write in this file.
 *
 * `items` and `source` were added to this hook (bug fix; the confirmation bar this enabled is now
 * `features/mail/CreateEnquiryBar.tsx`, which replaced the original triage-form caller this fix
 * was written for): `create_enquiry`'s documented body DOES accept both — the empty `items: []`
 * this hook used to send unconditionally was just this hook's two original callers
 * (`RequirementsPage`/`RfqPage`'s "no mail" buttons) never having lines to send at create time,
 * not a limit of the endpoint. `source` defaults to `'manual'` for those two callers, unchanged;
 * `CreateEnquiryBar` passes `'mail'` — a value this file cannot verify against the live bench (it isn't in this task's
 * documented body, only in `docs/bosun-frappe-backend-spec.md`'s field list, context-only), but
 * the mock's own `create_enquiry` handler already branches on it
 * (`mock-server/bosun/handlers/enquiry.ts`), so sending it here is consistent with what both
 * sides expect rather than a guess made up on this side alone.
 *
 * ---- `mail_id` (2026-08-05) ----
 *
 * `create_enquiry` DOES accept `mail_id` — this was wrongly filed under "no home" alongside
 * `company`/`customer_currency`/`po_currency` (correction in `docs/bosun-required-api-round-2.md`
 * A2: only the three currency/company fields are genuinely absent; `mail_id` and `sales_person`
 * round-trip). Verified live: an enquiry created with `mail_id` set reads it back on `get_enquiry`
 * (`ENQ-2026-00005`); one created through the dialog before this fix reads `mail_id: null`
 * (`ENQ-2026-00007`/`-00008`). `CreateEnquiryBar` sends the `Communication` id of the mail it was
 * opened from, so the RFQ builder's `SourcePanel` (`features/rfq/SourcePanel.tsx`) can read the
 * source back via `mail.get_email`.
 */
export interface NewEnquiryHeader {
  customer?: string
  /**
   * ---- CORRECTION 2026-08-08: `create_enquiry` DOES accept `company` ----
   *
   * `Q7` recorded it as rejected, and `E11` was written on that basis. Both were wrong. It is in
   * the collection's own documented body (`Quoflow_Complete_API_v2`, `"company": "Bosun Marine
   * LLC"`) and it works live — probed with a throwaway enquiry, read back raw, then deleted:
   *
   *     POST create_enquiry {…, "company": "BEUREX TRADING COMPANY W.L.L"}
   *       → ENQ-2026-00025, company echoed
   *     GET  /api/resource/Enquiry/ENQ-2026-00025 → company persisted
   *     DELETE                                    → ok (404 after)
   *
   * This is the field that makes multi-company correct at the ROOT of the chain: RFQ, supplier
   * quotation, comparison and customer quotation all demand a company, and one recorded here is
   * one they cannot disagree about. `CreateEnquiryBar` requires the purchaser to pick it.
   */
  company?: string
  enquiry_type?: string
  delivery_location?: string | null
  /** The selling price list the delivery location implies — `lookups.get_delivery_locations`. */
  price_list?: string | null
  vessel?: string | null
  delivery_port?: string | null
  purchaser?: string
  sales_person?: string
  /** `Enquiry.enquiry_remarks` — the automatic path's marker (`AUTO_ENQUIRY_MARK`), 2026-09-21. */
  enquiry_remarks?: string
  /** `'manual'` unless the caller is creating from a mail message. */
  source?: 'manual' | 'mail'
  /** Coded lines to create the enquiry with. Omitted/empty means an empty draft, same as before. */
  items?: NewEnquiryItem[]
  /** The `Communication` this draft is being created from — see this file's own header,
   * "`mail_id` (2026-08-05)". `undefined`/omitted for a manual create, same as every other
   * optional header field here. */
  mail_id?: string
}

/**
 * The date CLAUDE.md's API conventions call for is site-LOCAL `"YYYY-MM-DD"`, not a UTC one —
 * `toISOString()` would shift a create made shortly either side of local midnight onto the wrong
 * calendar day. Built from the local getters, not `toISOString().slice(0, 10)` (fix round 1,
 * minor).
 */
export function todayLocalDate(): string {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/**
 * One line of `create_enquiry`'s `items` array. Same shape `useAddLine` below writes for a new
 * row — RFQ-routed by default, no `enquiry_row_no`/`name` (there is no existing row to key against
 * yet; the server assigns one for every row on a create).
 */
/*
  `toNewItemWireRow` / `buildCreateEnquiryWireBody` MOVED to `createEnquiryFlow.ts`, which owns the
  whole create sequence (body → create → assign → read back) and its one hard rule: once the
  document exists, no follow-up may throw. Re-exported here because that is where callers and tests
  have always imported it from.
*/
export { buildCreateEnquiryWireBody } from './createEnquiryFlow'
import { toNewItemWireRow } from './createEnquiryFlow'
import { nextSerial } from '../uomChoice'
import { applySupplierChange } from '../supplierAssignment'

export function useCreateEnquiry(options?: {
  /**
   * `warning` is set when the enquiry WAS created but a follow-up (assigning the purchaser,
   * reading the document back) did not land — see `createEnquiryFlow`. It is not an error: the
   * enquiry exists and `enquiry.id` is good to navigate to.
   */
  onSuccess?: (enquiry: Enquiry, warning?: string) => void
  onError?: (error: BosunApiError) => void
}) {
  const queryClient = useQueryClient()
  return useMutation<{ enquiry: Enquiry; warning?: string }, BosunApiError, NewEnquiryHeader | void>({
    mutationFn: withBosunError((header) =>
      createEnquiryFlow(header ?? undefined, todayLocalDate(), { callPost, callGet }),
    ),
    onSuccess: ({ enquiry, warning }) => {
      void queryClient.invalidateQueries({ queryKey: ['enquiries'] })
      options?.onSuccess?.(enquiry, warning)
    },
    onError: (error) => options?.onError?.(error),
  })
}

/* ---------- update_enquiry: status / remarks_to_supplier / header scalars ---------- */

/**
 * `items` is deliberately NOT a field here (fix round 1, IMPORTANT 5). It used to be, and
 * `callUpdateEnquiry` would build+send a full replacement `items` array with NO read and OUTSIDE
 * `withEnquiryLock` — an exported footgun that bypassed every race protection
 * `updateEnquiryItems`/`withEnquiryLock` exist for, reachable by any future caller of
 * `useUpdateEnquiry` even though nothing calls it today. The five line-mutation hooks below have
 * always gone through `updateEnquiryItems` directly, never through this type — so removing `items`
 * here costs nothing and closes the hole. A future caller that needs to touch `items` should go
 * through `updateEnquiryItems`, not add it back here. Adding the scalar header fields below does
 * NOT reopen that hole — none of them is `items`, and each is sent only when explicitly present on
 * the patch (see `buildUpdateEnquiryWireBody`), same as `status`/`remarks_to_supplier` always were.
 *
 * ---- CORRECTION 2026-08-04 ----
 *
 * `customer`, `sales_person`, `vessel`, `delivery_location`, `delivery_port`, `enquiry_type` and
 * `date` were added after measuring writes directly against `rfqdev.etoserp.com` (ENQ-2026-00004,
 * every value restored afterwards) — one representative field per Frappe fieldtype this card
 * needs: `delivery_location` (Link), `customer_reference_no` (Data, not itself a header-card
 * field but confirms the Data type), `transaction_date` (Date), `use_email_body` (Check),
 * `enquiry_type` (Select). All five round-tripped cleanly through `update_enquiry` — it accepts
 * any field on the document, not just its documented `{status, remarks_to_supplier, items}` body
 * (the user's own `priority` probe found the same thing independently). `vessel`/`delivery_port`/
 * `customer`/`sales_person` were not each individually tested — they're enabled here on the
 * strength of `delivery_location` (Link) and `customer_reference_no` (Data) covering their
 * fieldtypes, not on a per-field probe; see `EnquiryHeaderCard.tsx`'s header for which fields
 * this generalization covers and which it deliberately doesn't (`source`/`type`, `subject`,
 * `sender`, `company`, `department`, `company_series` — none of the last five has a wire field to
 * write to at all). `enquiry_type`'s Select is restricted to `Provision`/`Technical` in the UI —
 * see `toEnquiryType`'s own comment above for why the other two `EnquiryType` values are
 * unconfirmed against the server's real Select options.
 */
export interface UpdateEnquiryPatch {
  status?: EnquiryStatus
  remarks_to_supplier?: string
  customer?: string
  /** The requesting department. Written to the CUSTOM field `custom_customer_department` — see
   *  `buildUpdateEnquiryWireBody` for why the name matters. */
  department?: string
  /**
   * ---- CORRECTION 2026-08-08: `company` is writable, and always was ----
   *
   * Ask `E11` said "`Enquiry` has no `company` field, `create_enquiry` rejects it, so we pass a
   * hardcoded constant on every `create_rfq`". The first half is wrong, and it was wrong the same
   * way `E13`'s was: it was measured against the WRONG DOCTYPE. Bosun's enquiry is its own
   * `Enquiry` doctype (42 fields), not stock `Opportunity`. Re-measured against
   * `rfqdev.etoserp.com`:
   *
   *     DocField parent="Enquiry" fieldname="company"
   *       → {fieldtype: "Link", options: "Company", label: "Company", reqd: 0}   ← it exists
   *     PUT enquiry.update_enquiry {"name":"ENQ-2026-00013","data":{"company":"AQUA MARIS …"}}
   *       → 200
   *     GET /api/resource/Enquiry/ENQ-2026-00013
   *       → company: "AQUA MARIS SHIPS SUPPLY -(S.P.S - L.L.C)"                  ← it PERSISTED
   *
   * So under multi-company the right answer can be recorded per enquiry today, by the person who
   * knows it, and `runRfqGeneration` reads it back instead of stamping `FALLBACK_COMPANY` on every
   * supplier-facing document. `create_enquiry` still refusing the field (`Q7`) is unchanged and
   * unaffected: an enquiry created from a mail starts with no company and the purchaser sets it
   * here.
   */
  company?: string
  sales_person?: string
  vessel?: string | null
  delivery_location?: string | null
  delivery_port?: string | null
  enquiry_type?: EnquiryType
  /** → wire `transaction_date`, not `date` — see `buildUpdateEnquiryWireBody`. */
  date?: string
  use_email_body?: boolean
  /**
   * The customer's own reference for this enquiry (their PO / enquiry number) and its date.
   * Both persist through `update_enquiry` — probed on ENQ-2026-00040, 2026-09-14. `null` clears.
   */
  customer_reference_no?: string | null
  customer_reference_date?: string | null
  /**
   * What the customer will be QUOTED in. Defaults to the company currency at the header card and
   * is carried into the quotation's `customer_currency` when it is built — set here, once, by the
   * person who knows, rather than re-guessed on the quote page.
   */
  customer_currency?: string | null
  /**
   * The selling price list, implied by the delivery location (`lookups.get_delivery_locations`
   * carries one per location). Written when the location changes so the record says which list
   * the quotation priced from.
   */
  price_list?: string | null
}

/**
 * The exact `update_enquiry` wire body for a header-scalar patch — pulled out as a pure function,
 * same reasoning `buildCreateEnquiryWireBody` above uses, so the domain-name → wire-name
 * translation (`date` → `transaction_date`, `delivery_port` → `port_of_delivery`) is pinned by a
 * test rather than only exercised through a mocked `fetch`. A key is sent only when the patch sets
 * it — `undefined` means "leave alone", matching every other partial-patch shape in this file.
 */
export function buildUpdateEnquiryWireBody(patch: UpdateEnquiryPatch): Record<string, unknown> {
  const data: Record<string, unknown> = {}
  if (patch.status !== undefined) data.status = patch.status
  if (patch.remarks_to_supplier !== undefined) data.remarks_to_supplier = patch.remarks_to_supplier
  if (patch.customer !== undefined) data.customer = patch.customer
  if (patch.company !== undefined) data.company = patch.company
  if (patch.sales_person !== undefined) data.sales_person = patch.sales_person
  if (patch.vessel !== undefined) data.vessel = patch.vessel
  if (patch.delivery_location !== undefined) data.delivery_location = patch.delivery_location
  if (patch.delivery_port !== undefined) data.port_of_delivery = patch.delivery_port
  if (patch.enquiry_type !== undefined) data.enquiry_type = patch.enquiry_type
  /*
    The CUSTOM field name, not `department` and not `customer_department`. The first is our domain
    name for it; the second is stock ERPNext's `Opportunity` field, which this doctype does not
    have — writing that is what made the department look unsavable for a month. Verified writable
    on ENQ-2026-00027, 2026-09-07.
  */
  if (patch.department !== undefined) data.custom_customer_department = patch.department
  if (patch.date !== undefined) data.transaction_date = patch.date
  if (patch.use_email_body !== undefined) data.use_email_body = patch.use_email_body ? 1 : 0
  if (patch.customer_reference_no !== undefined) data.customer_reference_no = patch.customer_reference_no
  if (patch.customer_reference_date !== undefined) data.customer_reference_date = patch.customer_reference_date
  if (patch.customer_currency !== undefined) data.customer_currency = patch.customer_currency
  if (patch.price_list !== undefined) data.price_list = patch.price_list
  return data
}

async function callUpdateEnquiry(enquiryId: string, patch: UpdateEnquiryPatch): Promise<WireEnquiryDetail> {
  // Its own response, not a re-read — measured key-for-key identical to `get_enquiry`'s. See
  // `updateEnquiryItems`' "Two round trips, not four" note for the measurement and the reasoning.
  return callPut<WireEnquiryDetail>('enquiry', 'update_enquiry', {
    name: enquiryId,
    data: buildUpdateEnquiryWireBody(patch),
  })
}

/**
 * The generic mutation every scalar header-field edit on `EnquiryHeaderCard.tsx` now goes through
 * directly (CORRECTION 2026-08-04 — it used to have no caller; see this file's own module header
 * for what changed). The five line-mutation hooks below still call `updateEnquiryItems` directly
 * rather than through this hook, since they each need to inspect the read they took to compute
 * their own return value — that split is unchanged.
 */
export function useUpdateEnquiry(enquiryId: string, options?: Options<Enquiry, UpdateEnquiryPatch>) {
  return useEnquiryMutation(
    enquiryId,
    async (patch: UpdateEnquiryPatch) => {
      const after = toEnquiry(await callUpdateEnquiry(enquiryId, patch))
      seedEnquiry(enquiryId, after)
      return after
    },
    { ...options, seedsEnquiry: true },
  )
}

/* ---------- purchaser ---------- */

interface WireAssignPurchaser {
  enquiry: string
  purchaser: string
  assigned_at: string
}

export function useAssignPurchaser(enquiryId: string, options?: Options<{ purchaser: string }, string>) {
  return useEnquiryMutation(
    enquiryId,
    async (purchaser: string) => {
      const w = await callPost<WireAssignPurchaser>('enquiry', 'assign_purchaser', {
        enquiry: enquiryId,
        purchaser,
      })
      return { purchaser: w.purchaser }
    },
    options,
  )
}

/* ---------- the item read-modify-write primitive ---------- */

/** Per-enquiry promise chain — see the module header's "the read-modify-write race" section. */
const enquiryLocks = new Map<string, Promise<unknown>>()

function withEnquiryLock<T>(enquiryId: string, fn: () => Promise<T>): Promise<T> {
  const prior = enquiryLocks.get(enquiryId) ?? Promise.resolve()
  const next = prior.then(fn, fn)
  // Tracked separately from `next` so one failed link doesn't wedge the queue for the next
  // caller — but `next` itself still rejects for THIS caller.
  enquiryLocks.set(
    enquiryId,
    next.then(
      () => undefined,
      () => undefined,
    ),
  )
  return next
}

/**
 * `update_enquiry`'s writable item shape — built by PRESERVING the row exactly as read and
 * overriding only the keys a mutation actually owns, not by whitelisting a fixed field list (fix
 * round 1, IMPORTANT 4). The earlier version built a fresh `{item_code, qty, uom, route,
 * create_rfq, suppliers}` object from scratch: anything the server returns that this file has no
 * interface entry for — `WireEnquiryDetail` already has `file_mapping` as one example — was wiped
 * on EVERY line edit, not just an edit that touched that field. Spreading `item` first means an
 * unmodelled field on THIS row (present on the actual object at runtime even if the `WireEnquiryItem`
 * interface doesn't name it — TS erases types, not properties) survives by construction, and this
 * cannot regress the next time the backend adds a column this file hasn't caught up to yet.
 *
 * `route`/`create_rfq` are normalized through `toRoute` (fix round 1, IMPORTANT 3), not read off
 * `item.route` verbatim: a row can arrive `route: ''` with `create_rfq: 1` (`toRoute` in
 * `useEnquiries.ts` treats that as `'rfq'` on READ), and writing `route` back unverbatim while
 * independently recomputing `create_rfq` from it would silently flip such a row to `'contract'`
 * as collateral damage from an edit to a completely different line in the same wholesale write.
 *
 * `enquiry_row_no` is OMITTED for a brand-new row (no key = create, the same Frappe child-table
 * convention the old `/api/resource` transport used — see the deleted `updateOpportunityItems`'s
 * comment in git history) rather than sent blank.
 */
function toWireItemWrite(item: WireEnquiryItem): Record<string, unknown> {
  const route = toRoute(item)
  const w: Record<string, unknown> = { ...item, route, create_rfq: route === 'rfq' ? 1 : 0 }
  if (!item.enquiry_row_no) delete w.enquiry_row_no
  return w
}

/**
 * Read-modify-write against `update_enquiry`'s wholesale `items` replace — the ONE place this
 * file builds the `items` array `update_enquiry` sends. `modify` receives the CURRENT rows, as
 * `get_enquiry` returned them, and returns the full array to send back: a row it keeps survives
 * (`toWireItemWrite` above carries its `enquiry_row_no`), a row it leaves out is deleted, a row
 * with no `enquiry_row_no` is created.
 *
 * Serialized per enquiry via `withEnquiryLock` — see the module header.
 */
export async function updateEnquiryItems(
  enquiryId: string,
  modify: (items: WireEnquiryItem[]) => WireEnquiryItem[],
): Promise<{ before: WireEnquiryItem[]; after: Enquiry }> {
  return withEnquiryLock(enquiryId, async () => {
    const doc = await callGet<WireEnquiryDetail>('enquiry', 'get_enquiry', { name: enquiryId })
    const before = doc.items ?? []
    const nextItems = modify(before)
    /*
      ---- Two round trips, not four (2026-08-11) ----

      This used to read, write, then read AGAIN — and `useEnquiryMutation` then invalidated the
      enquiry query, making React Query fetch the very same document a FOURTH time. Every request
      on this bench costs ~0.85s whatever it is (measured: get_enquiry 0.77–0.93s, update_enquiry
      0.86s, payload size irrelevant), so a single qty edit cost ~3.4s of sequential waiting, of
      which half bought nothing.

      Both extra trips are gone:

      * The re-read is replaced by `update_enquiry`'s OWN response. The old comment here said that
        response was "unverified — re-read rather than trust it", which was honest when written.
        Measured now, on a throwaway enquiry: the PUT body is key-for-key IDENTICAL to
        `get_enquiry`'s, including the computed `age_days`, `ref`, `rfq_count` and `created_at`,
        and its `items` carry the written values. Nothing is lost by trusting it.
      * The invalidation is replaced by seeding the cache with that same document, so the screen
        re-renders from what we already hold instead of asking for it again.

      The pre-read stays and must: `items` replaces WHOLESALE, so a row we did not read is a row
      we would delete. It is also what makes `withEnquiryLock` meaningful.
    */
    const written = await callPut<WireEnquiryDetail>('enquiry', 'update_enquiry', {
      name: enquiryId,
      data: { items: nextItems.map(toWireItemWrite) },
    })
    const after = toEnquiry(written)
    seedEnquiry(enquiryId, after)
    return { before, after }
  })
}

/* ---------- lines ---------- */

function applyItemPatch(row: WireEnquiryItem, patch: EnquiryItemPatch): WireEnquiryItem {
  return {
    ...row,
    ...(patch.item_code !== undefined ? { item_code: patch.item_code } : {}),
    ...(patch.description !== undefined ? { description: patch.description } : {}),
    ...(patch.customer_uom !== undefined ? { customer_uom: patch.customer_uom } : {}),
    ...(patch.conversion_factor !== undefined ? { conversion_factor: patch.conversion_factor } : {}),
    ...(patch.qty_as_per_stock_uom !== undefined ? { qty_as_per_stock_uom: patch.qty_as_per_stock_uom } : {}),
    ...(patch.qty !== undefined ? { qty: patch.qty } : {}),
    ...(patch.uom !== undefined ? { uom: patch.uom } : {}),
    ...(patch.note !== undefined ? { note: patch.note } : {}),
    ...(patch.item_creation_req_ref !== undefined ? { item_creation_req_ref: patch.item_creation_req_ref } : {}),
    // `contract_id` is dropped on purpose: this wire has no per-line contract link to write to
    // either (same gap `useEnquiries.ts`'s `toEnquiryItem` documents on the read side).
    ...(patch.route !== undefined ? { route: patch.route } : {}),
    ...(patch.selected_rate !== undefined ? { selected_rate: patch.selected_rate } : {}),
    // The matcher's own score for the item being applied — see `EnquiryItemPatch.confidence`.
    ...(patch.confidence !== undefined ? { extraction_confidence: patch.confidence } : {}),
    ...(patch.suppliers !== undefined ? { suppliers: patch.suppliers } : {}),
  }
}

/**
 * Many lines patched in ONE pass over the items array.
 *
 * `update_enquiry` replaces `items` WHOLESALE, which makes a bulk change no more expensive on the
 * wire than a single one — so N changed lines can be one request instead of N read-modify-writes.
 * On this bench every request costs ~0.85s regardless of payload, so a line patched on its own
 * costs ~1.7s (`get_enquiry` + `update_enquiry`) and seventeen of them cost ~29s of nothing but
 * waiting; batched, the whole enquiry costs the same ~1.7s as one line.
 *
 * Every row is returned, patched or not: a row left out is a row `update_enquiry` DELETES. A
 * patch naming a row that is no longer there is ignored rather than appended — a row with no
 * `enquiry_row_no` would be CREATED, which would silently add a line nobody asked for.
 */
export function applyItemPatches(
  items: WireEnquiryItem[],
  byLineId: ReadonlyMap<string, EnquiryItemPatch>,
): WireEnquiryItem[] {
  if (byLineId.size === 0) return items
  return items.map((row) => {
    const patch = byLineId.get(row.enquiry_row_no as string)
    return patch ? applyItemPatch(row, patch) : row
  })
}

/**
 * The bulk sibling of `useUpdateLine` — see `applyItemPatches` for why this exists.
 *
 * Used by `AutoMatcher`, which resolves every line's item, unit and suppliers concurrently and
 * then commits the lot. The single-line hook is unchanged and still the right call for a single
 * edit; this is strictly for the case where N lines change at once.
 */
export function useUpdateLines(
  enquiryId: string,
  options?: Options<Enquiry, Array<{ lineId: string; patch: EnquiryItemPatch }>>,
) {
  return useEnquiryMutation(
    enquiryId,
    async (patches: Array<{ lineId: string; patch: EnquiryItemPatch }>) => {
      const byLineId = new Map(patches.map((p) => [p.lineId, p.patch]))
      const { after } = await updateEnquiryItems(enquiryId, (items) => applyItemPatches(items, byLineId))
      return after
    },
    // `updateEnquiryItems` already seeded the cache with the post-write document.
    { ...options, seedsEnquiry: true },
  )
}

export function useUpdateLine(
  enquiryId: string,
  options?: Options<PatchedLine, { lineId: string; patch: EnquiryItemPatch }>,
) {
  return useEnquiryMutation(
    enquiryId,
    async ({ lineId, patch }: { lineId: string; patch: EnquiryItemPatch }) => {
      const { after } = await updateEnquiryItems(enquiryId, (items) =>
        items.map((row) => (row.enquiry_row_no === lineId ? applyItemPatch(row, patch) : row)),
      )
      const line = after.items.find((item) => item.line_id === lineId)
      if (!line) throw new Error(`Line ${lineId} no longer exists on ${enquiryId} after update`)
      // No warnings field on this wire — see `PatchedLine`'s own comment.
      return line
    },
    // `updateEnquiryItems` already seeded the cache with the post-write document.
      { ...options, seedsEnquiry: true },
  )
}

/**
 * The rows an add appends — one or many — to the rows as READ, inside the lock.
 *
 * NUMBERED, not left to the server (Akhil, 2026-09-15): each new line takes the next serial
 * after the highest so far, so it reads as line N+1 on the RFQ and the quotation. Every field the
 * caller has travels (2026-09-17): the customer's wording (`raw_text` — the upload path had been
 * losing it, leaving rows with a quantity and nothing to match), the unit as both `uom` and
 * `customer_uom` when it is a real UOM master value, and `null` for a unit the master does not
 * know — both are Links, and one unknown value fails the WHOLE wholesale write.
 */
function appendedRows(items: readonly WireEnquiryItem[], adds: readonly NewEnquiryItem[]): WireEnquiryItem[] {
  let out: WireEnquiryItem[] = [...items]
  for (const body of adds) {
    const serial = body.at?.trim() || nextSerial(out.map((row) => ({ cust_sno: str(row.enquiry_row_no) })))
    const row = {
      name: '',
      enquiry_row_no: serial,
      item_code: body.item_code,
      item_name: '',
      qty: body.qty,
      uom: body.uom,
      route: 'rfq',
      create_rfq: 1,
      suppliers: body.suppliers ?? [],
      ...(body.raw_text ? { raw_text: body.raw_text } : {}),
      ...(body.description ? { description: body.description } : {}),
      ...(body.customer_uom ? { customer_uom: body.customer_uom } : {}),
      // A code the caller already has — from the customer's own list, or a hand pick — is a
      // decision: confidence 1 reads "chosen" on the row. Left at 0 it drew a red 0% bar under a
      // perfectly good item (Akhil, 2026-09-17). A matcher's score, when given, is kept.
      ...(body.confidence !== undefined ? { extraction_confidence: body.confidence } : body.item_code ? { extraction_confidence: 1 } : {}),
    } as WireEnquiryItem
    // A chosen number goes IN at that number (`insertLine.ts`): the lines from it move down one,
    // and every row carries an `idx` so the server keeps the order. Otherwise appended.
    out = body.at?.trim() ? insertLineAt(out, row, serial) : [...out, row]
  }
  return out
}

/**
 * MANY lines in ONE write — an uploaded list (2026-09-17). Seventy lines used to be seventy
 * read-modify-writes in sequence, ~2s each, and the first unit outside the UOM master failed
 * the rest. Returns the lines that were added, in order.
 */
export function useAddLines(enquiryId: string, options?: Options<EnquiryItem[], NewEnquiryItem[]>) {
  return useEnquiryMutation(
    enquiryId,
    async (adds: NewEnquiryItem[]) => {
      if (adds.length === 0) return []
      const { before, after } = await updateEnquiryItems(enquiryId, (items) => appendedRows(items, adds))
      const existingIds = new Set(before.map((row) => row.enquiry_row_no))
      return after.items.filter((item) => !existingIds.has(item.line_id))
    },
    { ...options, seedsEnquiry: true },
  )
}

export function useAddLine(enquiryId: string, options?: Options<EnquiryItem, NewEnquiryItem>) {
  return useEnquiryMutation(
    enquiryId,
    async (body: NewEnquiryItem) => {
      // `NewEnquiryItem.uom` widened to `string | null` for `create_enquiry` (an unmatched line
      // can travel with no uom — see toNewItemWireRow). This mutation instead ADDS a line to an
      // ALREADY-EXISTING enquiry via the picker in AddItemModal.tsx, which only ever offers a
      // real catalogue uom — so a null here means a caller regressed that contract, not a
      // legitimate unmatched line (those belong in `create_enquiry`'s items array, not here).
      if (body.uom === null) {
        throw new Error(`useAddLine requires a resolved uom (got null for ${body.item_code ?? 'a line with no item_code'})`)
      }
      const { before, after } = await updateEnquiryItems(enquiryId, (items) => appendedRows(items, [body]))
      const existingIds = new Set(before.map((row) => row.enquiry_row_no))
      const added = after.items.find((item) => !existingIds.has(item.line_id))
      if (!added) throw new Error(`New ${body.item_code} line was not found on ${enquiryId} after adding it`)
      return added
    },
    // `updateEnquiryItems` already seeded the cache with the post-write document.
      { ...options, seedsEnquiry: true },
  )
}

export function useDeleteLine(enquiryId: string, options?: Options<void, string>) {
  return useEnquiryMutation(
    enquiryId,
    async (lineId: string) => {
      await updateEnquiryItems(enquiryId, (items) => items.filter((row) => row.enquiry_row_no !== lineId))
    },
    // `updateEnquiryItems` already seeded the cache with the post-write document.
      { ...options, seedsEnquiry: true },
  )
}

/** Several lines gone in ONE wholesale write (Akhil, 2026-09-21: "a way to delete multiple items"). */
export function useDeleteLines(enquiryId: string, options?: Options<void, string[]>) {
  return useEnquiryMutation(
    enquiryId,
    async (lineIds: string[]) => {
      const gone = new Set(lineIds)
      await updateEnquiryItems(enquiryId, (items) => items.filter((row) => !gone.has(String(row.enquiry_row_no))))
    },
    { ...options, seedsEnquiry: true },
  )
}

/* ---------- bulk ---------- */

/**
 * Adds suppliers to every named line's EXISTING list (union, not replace — matching the old
 * bespoke endpoint's behaviour), or removes / clears them (`mode`, 2026-09-15), skipping
 * contract-routed lines, which need no RFQ. The pass itself is `applySupplierChange`.
 */
export function useAssignSuppliers(enquiryId: string, options?: Options<{ items: EnquiryItem[] }, AssignSuppliersPayload>) {
  return useEnquiryMutation(
    enquiryId,
    async ({ line_ids: lineIds, suppliers, mode = 'add' }: AssignSuppliersPayload) => {
      const touched = new Set(lineIds)
      const { after } = await updateEnquiryItems(enquiryId, (items) => applySupplierChange(items, lineIds, suppliers, mode))
      return { items: after.items.filter((item) => touched.has(item.line_id)) }
    },
    // `updateEnquiryItems` already seeded the cache with the post-write document.
      { ...options, seedsEnquiry: true },
  )
}

/**
 * Points several lines at the same matched item, in one write — each line keeps its own qty.
 * Only `item_code` changes; `route`/`uom` are left exactly as they were, same reasoning the old
 * version used (re-deriving a route/UOM default is server-side business logic this file has no
 * business reimplementing).
 */
export function useMatchLines(enquiryId: string, options?: Options<{ items: EnquiryItem[] }, MatchLinesPayload>) {
  return useEnquiryMutation(
    enquiryId,
    async ({ line_ids: lineIds, item_code: itemCode, uom_by_line: uomByLine, suppliers }: MatchLinesPayload) => {
      const touched = new Set(lineIds)
      const { after } = await updateEnquiryItems(enquiryId, (items) =>
        items.map((row) => {
          if (!touched.has(row.enquiry_row_no)) return row
          // The unit rides along in the SAME wholesale write — see `MatchLinesPayload.uom_by_line`.
          // A row absent from the map keeps the unit it has; the caller only lists what changes.
          const uom = uomByLine?.[row.enquiry_row_no]
          return {
            ...row,
            item_code: itemCode,
            ...(uom ? { uom } : {}),
            ...(suppliers && suppliers.length > 0 ? { suppliers } : {}),
          }
        }),
      )
      return { items: after.items.filter((item) => touched.has(item.line_id)) }
    },
    // `updateEnquiryItems` already seeded the cache with the post-write document.
      { ...options, seedsEnquiry: true },
  )
}

/* ---------- extraction + RFQ generation — LEFT ON THE OLD CLIENT ---------- */

/**
 * The "No mail? Upload the customer's list" button on the RFQ screen. LEFT ON THE OLD CLIENT —
 * see the module header: `mail.extract_enquiry_items` needs an existing mail id this call site
 * has none of.
 *
 * A3 (docs/bosun-required-api-round-2.md) — the specific ask that unblocks this hook. Not
 * proxied live (`vite.config.ts` routes `/api` wholesale now); `RfqPage.tsx`'s `UploadButton`
 * disables itself via `lib/api/liveAvailability.ts` rather than calling this and 404ing. Kept
 * as-is, unguarded, so it's ready to call the moment the endpoint exists.
 */
export function useExtractFile(enquiryId: string, options?: Options<ExtractResponse, File>) {
  return useEnquiryMutation(
    enquiryId,
    (file: File) => {
      const form = new FormData()
      form.append('file', file)
      return api.postForm<ExtractResponse>(`${base(enquiryId)}/extract`, form)
    },
    options,
  )
}

/* ---------- RFQ generation + sending (Task 8) ---------- */

/**
 * ⚠️ NOT CALLED TODAY — `generate_rfqs` is dead (see the CLIENT-SIDE RFQ GENERATION section
 * below for why, and `docs/superpowers/specs/2026-08-06-client-side-rfq-generation-design.md`).
 * `WireGeneratedRfq`/`toGeneratedRfq` are kept, with their tests, because they are the response
 * shape of the endpoint we go back to the day `Bosun Settings.company` ships: restoring it is
 * then deleting the stopgap and calling `callPost('rfq', 'generate_rfqs', { enquiry })` here
 * again. Do not build anything new on this mapper in the meantime.
 *
 * `rfq.generate_rfqs` — creates or updates one RFQ per supplier from this enquiry's RFQ-routed,
 * supplier-assigned lines, with a stub `excel_url`. Takes ONLY `{enquiry}` — no covering-mail
 * text — because generating the RFQ record is a different act from mailing it; `useSendRfq` below
 * is the only call that actually sends.
 *
 * This REPLACES the deprecated version of this hook, which POSTed a single `{messages}` payload
 * to the old `/enquiries/{id}/rfqs` route and got back RFQs that were ALREADY sent — one call
 * doing both "create the RFQ" and "compose + mail it from client-supplied text". That collapsed
 * exactly the boundary `send_rfq`'s five-field body exists to enforce (see `SendRfqPayload`'s
 * comment), so it is not carried forward: this hook now only ever creates/updates the RFQ
 * records, and a caller must follow up with `useSendRfq` per RFQ to actually send.
 *
 * The response carries the enquiry's new status — `RFQ Sent` is a side effect of GENERATING on
 * this API, not of sending.
 *
 * `generate_rfqs`' row shape is NOT `rfq.get_rfqs`'s `WireRfq` — the id key is `id` here, not
 * `name`, there is no `supplier_name`/`req_tag`, and there is no `items` snapshot (nothing to
 * send yet). Kept as its own interface rather than reused, so a real field mismatch between the
 * two endpoints shows up as a type error instead of a silently-undefined property.
 *
 * The live response also carries `email_to`, but it's NOT modeled here (review round 1, Minor 2):
 * nothing in this file's actual data flow reads it — `sendAllDrafts`/`sendRfqDrafts` (`lib/rfq.
 * ts`) only ever use `.id`/`.supplier` off this response — and the real address comes from
 * `items.get_supplier_emails` regardless (see `useSendRfq`'s own comment), so parsing a second,
 * untrusted email field here would just invite a future reader to assume it's the one in use.
 */
export interface WireGeneratedRfq {
  id: string
  supplier: string
  item_count: number
  excel_url?: string | null
  status: string
  sent_at?: string | null
}

function toGeneratedRfq(w: WireGeneratedRfq): Rfq {
  return {
    id: w.id,
    supplier: w.supplier,
    item_count: num(w.item_count),
    excel_url: str(w.excel_url),
    email_to: '', // deliberately not sourced from the wire's `email_to` — see this fn's own header
    status: toRfqStatus(w.status),
    sent_at: w.sent_at ?? null,
    // No line snapshot on this response — `RfqTemplateCard` (fed by `useRfqs`, not this hook)
    // is what shows it, not the generate-RFQs result.
    items: undefined,
  }
}

/* ============================================================================================
 * CLIENT-SIDE RFQ GENERATION — a stopgap that REPLACES `rfq.generate_rfqs`. DELETE IT LATER.
 * ============================================================================================
 *
 * `rfq.generate_rfqs` is dead on the live bench (2026-08-05): it reads `Bosun Settings.company`,
 * a field that does not exist there, so every call 500s with
 * `ValidationError: Field company does not exist on Bosun Settings`. **Sending `company` in the
 * request body does not help — measured, not assumed**: the method reads the settings doc
 * server-side and ignores the request field entirely. Everything downstream of the enquiry (RFQ →
 * supplier quotation → comparison → quotation) is blocked behind it.
 *
 * So the grouping the server used to do — which lines go to which supplier, and whether that
 * supplier already has an RFQ — happens here instead, over three methods that DO work
 * (`rfq.get_rfqs`, `rfq.create_rfq`, `rfq.update_rfq`, all verified live 2026-08-06). Only the
 * grouping moved. The workbook and the mail stay server-side in `send_rfq`, untouched.
 *
 * **When `Bosun Settings.company` ships, delete this whole section** — `generateRfqs` below,
 * `toExistingRfq`, `GenerateRfqsResult`, `src/lib/api/company.ts`, and `buildRfqPlan`/
 * `buildRfqItemRows` in `src/lib/rfq.ts` — and put `useGenerateRfqs`' body back to the single
 * `callPost('rfq', 'generate_rfqs', { enquiry })` + `toGeneratedRfq` mapping kept just above.
 * This is a route around a one-field backend bug, not a design preference, and keeping both paths
 * alive in parallel is the one outcome that would make it one.
 */

/** What one supplier's already-sent RFQ cost us: nothing was written, and these lines went nowhere. */
export interface SkippedRfq {
  supplier: string
  rfq: string
  /** How many lines were left out — the purchaser has to be told, not just that we skipped. */
  lines: number
}

export interface FailedRfq {
  supplier: string
  message: string
}

/**
 * `GenerateRfqsResponse` plus the two outcomes a client-side loop has and a single server call
 * did not: suppliers deliberately left alone, and suppliers whose write failed while others
 * succeeded. `enquiry_status` is nullable here for the same reason — when nothing succeeded the
 * status is deliberately NOT moved, and reporting `'RFQ Sent'` anyway would be a lie.
 */
export interface GenerateRfqsResult {
  /** Created or updated this round. NOT the skipped ones — those must not be re-sent. */
  rfqs: Rfq[]
  enquiry_status: EnquiryStatus | null
  skipped: SkippedRfq[]
  failed: FailedRfq[]
  /** Set when the RFQs were written but moving the enquiry status afterwards failed. */
  status_error?: string
}

/**
 * One `rfq.get_rfqs` row → the minimum `buildRfqPlan` needs, mapped DIRECTLY off the wire and
 * never through `toRfq` — that mapper runs `status` through `toRfqStatus`, which collapses `Draft`
 * onto `'sent'`, so a mapped `Rfq` cannot answer the one question this decision turns on.
 *
 * Three refusals, each of which would otherwise mail a supplier twice:
 *
 * 1. **`supplier`, never `supplier_name`.** Live, `supplier` is `"Top Go FZE"` while
 *    `supplier_name` is `" Top Go FZE"` — with a leading space (the documented wire defect, see
 *    `wire.ts`'s `trimmed()`). `EnquiryItem.suppliers[]` carries the Link identity, so matching on
 *    the display name matches nothing, every run looks like a first run, and every run creates a
 *    SECOND RFQ for a supplier who already has one.
 * 2. **A missing `sent_at` key throws.** It is optional on `WireRfq`, and `row.sent_at ?? null`
 *    would read a server that stopped sending the key as "none of these was ever sent" — so
 *    `update_rfq` would rewrite, wholesale, the items of an RFQ a supplier has already been mailed.
 *    That is precisely the failure `buildRfqPlan` exists to prevent, entering through the one door
 *    it cannot watch. Key-present-but-`null` is a genuine "not sent yet" and maps to `null`.
 * 3. **A blank, whitespace-only, or whitespace-PADDED `name`/`supplier` throws.** An unmatchable row
 *    is indistinguishable from no row at all, and "no row" means "create one" — the same double-RFQ
 *    outcome as (1), silently. See `requireCleanLink` for why padding fails closed rather than being
 *    trimmed away.
 */

/**
 * A Frappe Link value as it must arrive to be usable as an identity: present, non-blank, and
 * carrying no surrounding whitespace.
 *
 * **Padding fails closed; it is deliberately NOT trimmed away.** Two reasons, both learned from this
 * wire:
 *
 * - A silent trim would hide a real server-side defect that we would then keep discovering
 *   downstream. The live bench demonstrably does this to the sibling field — `supplier` is
 *   `"Top Go FZE"` while `supplier_name` is `" Top Go FZE"` on the same row (measured 2026-08-06),
 *   which is why `wire.ts` has `trimmed()` at all. `trimmed()` is right for a DISPLAY name; an
 *   identity is a different thing.
 * - A trimmed Link is a DIFFERENT Link. `" Top Go FZE"` matching `EnquiryItem.suppliers[]`'s
 *   `"Top Go FZE"` only because we normalised it means we would go on to send the normalised value
 *   back to `update_rfq`/`create_rfq` as a docname — and a Frappe Link must match character for
 *   character. Guessing which of the two spellings is the real document is not this function's call
 *   to make, and getting it wrong writes to the wrong document.
 *
 * Untrimmed and unthrown, the value would simply never match in `buildRfqPlan`, the plan would say
 * `create`, and a supplier who already has an RFQ would get a second one — and a second mail.
 */
function requireCleanLink(field: 'name' | 'supplier', value: unknown): string {
  if (typeof value !== 'string' || value.trim() === '') {
    throw new Error(
      `rfq.get_rfqs returned a row with no ${field} — refusing to generate RFQs against a list this ` +
        'app cannot match, which would create a second RFQ for a supplier who already has one.',
    )
  }
  if (value !== value.trim()) {
    throw new Error(
      `rfq.get_rfqs returned ${field} "${value}" with surrounding whitespace — refusing to guess ` +
        'which spelling is the real document. Padded, it matches nothing and every run raises a ' +
        'second RFQ for a supplier who already has one; trimmed, it is a different Frappe Link. ' +
        'This is a server-side defect and needs fixing there.',
    )
  }
  return value
}

export function toExistingRfq(row: WireRfq): ExistingRfq {
  const rfq = requireCleanLink('name', row?.name)
  const supplier = requireCleanLink('supplier', row?.supplier)
  const sentAt = row.sent_at
  if (!('sent_at' in row) || sentAt === undefined) {
    throw new Error(
      `rfq.get_rfqs returned ${rfq} with no sent_at key — refusing to guess whether that RFQ has ` +
        'already been mailed. Treating it as unsent would rewrite the items a supplier was already asked to quote.',
    )
  }
  // The rows the supplier was asked to quote — what lets `buildRfqPlan` send a later add on a
  // supplementary RFQ instead of skipping the supplier wholesale.
  const rows = (row.items ?? []).map((i) => str(i.enquiry_row_no)).filter((r) => r !== '')
  return { rfq, supplier, sent_at: sentAt, rows }
}

/** `create_rfq`/`update_rfq`'s response — the same three keys from both (verified live 2026-08-06). */
interface WireWrittenRfq {
  name: string
  supplier?: string
  status?: string
}

/**
 * The replacement for one `generate_rfqs` call: read what exists, plan, write, report.
 *
 * Deliberate choices, each one load-bearing:
 *
 * - **The existing-RFQ list is read with `callGet`, straight from the server, never from React
 *   Query's cache** (`useRfqs`' `qk.rfqs(id)` entry). A cached list one write out of date is
 *   exactly how a supplier who already has an RFQ gets a second one — and a second mail.
 * - **Sequential, not `Promise.all`.** These are writes against one enquiry; concurrency buys
 *   nothing and makes a partial failure much harder to report honestly.
 * - **Every action is isolated.** Supplier B's failure must not lose supplier C, and must not
 *   un-report supplier A's success — the RFQ A got is real and has to be named as such.
 * - **The status only moves when something actually succeeded.** `generate_rfqs` moved it as a
 *   side effect of generating; a round where every write failed generated nothing.
 * - **`send_rfq` is never called from here.** Creating an RFQ and mailing it are separate acts.
 * - **The whole read-plan-write is serialized per enquiry** — see `generateRfqs` below.
 */
/**
 * RFQ ids the server currently records as SENT, for this enquiry.
 *
 * Read at the moment of a retry (`sendRfqDrafts`' `alreadySent`), because that is the only way to
 * learn about a send that succeeded on the server while the client saw a failure — a timeout, a
 * 502, a body returned after the mail had already gone. Without it a retry mails that supplier a
 * second time.
 *
 * Fails CLOSED by construction: `readRfqList` throws on an unreadable payload rather than reading
 * it as "no RFQs", and `toExistingRfq` throws when the `sent_at` KEY is absent rather than treating
 * a missing key as "not sent". Both are what make an empty result trustworthy.
 */
export async function fetchSentRfqIds(enquiryId: string): Promise<Set<string>> {
  const res = await callGet<unknown>('rfq', 'get_rfqs', { enquiry: enquiryId })
  const existing = readRfqList(res, enquiryId).map(toExistingRfq)
  return new Set(existing.filter((rfq) => rfq.sent_at !== null).map((rfq) => rfq.rfq))
}

/**
 * The company to stamp on this enquiry's RFQs.
 *
 * A read failure falls back rather than throwing: `create_rfq` rejects a missing `company`
 * outright, so a transient error on this GET would turn "generate RFQs" into "generate nothing".
 * The fallback is wrong under multi-company (see `company.ts`) but recoverable — an RFQ under the
 * wrong entity can be corrected; an enquiry that cannot raise one at all cannot.
 */
export async function resolveEnquiryCompany(enquiryId: string): Promise<string> {
  return (await resolveEnquiryHeaderForRfq(enquiryId)).company
}

/** What an RFQ copies from its enquiry — read once per generation, defaults when the read fails. */
async function resolveEnquiryHeaderForRfq(enquiryId: string): Promise<{ company: string; purchaser: string; customer_currency: string }> {
  try {
    const w = await callGet<WireEnquiryDetail & { purchaser?: string | null; customer_currency?: string | null }>('enquiry', 'get_enquiry', { name: enquiryId })
    const company = typeof w?.company === 'string' ? w.company.trim() : ''
    return {
      company: company === '' ? getCompany() : company,
      purchaser: typeof w?.purchaser === 'string' ? w.purchaser.trim() : '',
      customer_currency: typeof w?.customer_currency === 'string' ? w.customer_currency.trim() : '',
    }
  } catch {
    return { company: getCompany(), purchaser: '', customer_currency: '' }
  }
}

/**
 * The supplier's display name and address for the RFQ header (Akhil, 2026-09-17: "make sure
 * you are passing the supplier email id and supplier name"). `items.get_supplier_emails` is the
 * one source the app has for a supplier's address (`useItems.ts`'s own note: `Supplier.email_id`
 * is empty live). A failed lookup leaves both out — the RFQ is still raised; the send resolves
 * the address again anyway.
 */
async function resolveSupplierForRfq(supplier: string): Promise<{ supplier_name?: string; supplier_email?: string }> {
  let name = ''
  try {
    const w = await callGet<{ supplier_name?: string | null; email?: string | null }>('items', 'get_supplier_emails', { supplier })
    name = typeof w?.supplier_name === 'string' ? w.supplier_name.trim() : ''
  } catch {
    name = ''
  }
  // The address through the same lookup the send uses (2026-09-22): the server's, then the
  // supplier's Contact documents, where most of them actually are.
  const email = (await lookupSupplierAddresses(supplier))?.to[0] ?? ''
  return { ...(name ? { supplier_name: name } : {}), ...(email ? { supplier_email: email } : {}) }
}

/**
 * The catalogue's own name for each matched item — what the supplier's workbook prints.
 *
 * FAILS SOFT to an empty map, and that is safe rather than merely tolerable: `buildRfqItemRows`
 * falls back to the customer's text with our internal annotations stripped, which is still a
 * correct description. A failure here costs the master name, never the fix (point 11).
 *
 * One `in` query, not one per line: an enquiry routinely carries dozens of rows.
 */
async function resolveItemNames(codes: string[]): Promise<Map<string, string>> {
  const unique = [...new Set(codes.filter((c) => c !== ''))]
  if (unique.length === 0) return new Map()
  try {
    const page = await getResource<{ name?: unknown; item_name?: unknown }>('Item', {
      fields: ['name', 'item_name'],
      filters: [['name', 'in', unique] as [string, string, unknown]],
      limit: unique.length,
    })
    return new Map(
      page.data
        .map((row) => [str(row.name), str(row.item_name)] as const)
        .filter(([code, name]) => code !== '' && name !== ''),
    )
  } catch {
    return new Map()
  }
}

async function runRfqGeneration(enquiryId: string, lines: EnquiryItem[]): Promise<GenerateRfqsResult> {
  const res = await callGet<unknown>('rfq', 'get_rfqs', { enquiry: enquiryId })
  // The list itself fails CLOSED, exactly like every row in it (`toExistingRfq`). An unreadable
  // response read as "this enquiry has no RFQs" would make the plan say `create` for EVERY
  // supplier, so everyone who already has one would get a second RFQ and a second mail — the one
  // unrecoverable outcome this function exists to prevent. `readRfqList` throws instead.
  //
  // It also owns the shape, and that matters: this line used to read `res?.rfqs`, which against
  // the live server (whose payload is a BARE ARRAY) is `undefined` every time. The guard is what
  // surfaced it — it fired on a real enquiry rather than silently duplicating. An empty list from
  // the server is still a real answer and still succeeds.
  const existing = readRfqList(res, enquiryId).map(toExistingRfq)
  const plan = buildRfqPlan(lines, existing)

  if (plan.length === 0) {
    // Not a silent success. Zero RFQs from a button labelled "generate RFQs" is a fact the
    // purchaser has to be told, along with the only thing that fixes it.
    throw new Error(
      'No RFQ was generated: no RFQ-routed line has both a matched item and a supplier assigned. ' +
        'Match the lines and assign a supplier to at least one of them first.',
    )
  }

  // The enquiry's OWN company, not a module constant — re-read here rather than taken from the
  // caller so a stale prop cannot stamp the wrong legal entity on a supplier-facing document.
  // Falls back to `FALLBACK_COMPANY` only when the enquiry genuinely has none (every enquiry
  // Bosun created before the header picker existed, since `create_enquiry` refuses the field);
  // `EnquiryHeaderCard` says so on screen, so the purchaser can fix it in one pick.
  const header = await resolveEnquiryHeaderForRfq(enquiryId)
  const company = header.company
  const baseCurrency = getBaseCurrency()
  // Fetched once for the whole plan, not per supplier: the same item can appear on several
  // suppliers' RFQs, and every one of them should print the same description.
  const itemNames = await resolveItemNames(
    plan.flatMap((action) => (action.kind === 'skip-sent' ? [] : action.lines.map((l) => l.item_code ?? ''))),
  )
  const transactionDate = todayLocalDate()
  const rfqs: Rfq[] = []
  const skipped: SkippedRfq[] = []
  const failed: FailedRfq[] = []

  for (const action of plan) {
    if (action.kind === 'skip-sent') {
      skipped.push({ supplier: action.supplier, rfq: action.rfq, lines: action.lines.length })
      continue
    }
    const items = buildRfqItemRows(action.lines, itemNames)
    try {
      const who = action.kind === 'create' ? await resolveSupplierForRfq(action.supplier) : {}
      const w =
        action.kind === 'create'
          ? await callPost<WireWrittenRfq>('rfq', 'create_rfq', {
              data: {
                status: 'Draft',
                enquiry: enquiryId,
                supplier: action.supplier,
                // Every header field the doctype carries that the app can know (2026-09-17).
                ...who,
                ...(header.purchaser ? { purchaser: header.purchaser } : {}),
                currency: baseCurrency,
                customer_currency: header.customer_currency || baseCurrency,
                // The field `generate_rfqs` cannot read for itself — see this section's header.
                company,
                transaction_date: transactionDate,
                items,
              },
            })
          : // POST, not PUT — same verb defect as `save_comparison`. `PUT rfq.update_rfq` answers
            // `PermissionError: Not permitted`, which was filed as `D10` ("a regression"); `POST`
            // with a bogus name answers `DoesNotExistError`, i.e. it reaches the business logic.
            // It was never a permissions problem.
            await callPost<WireWrittenRfq>('rfq', 'update_rfq', {
              name: action.rfq,
              // `update_rfq` replaces `items` WHOLESALE (verified live): every row that should
              // survive is in `items`, which is why `buildRfqPlan` never routes a sent RFQ here.
              data: { status: 'Draft', items },
            })
      const name = typeof w?.name === 'string' ? w.name.trim() : ''
      if (!name) throw new Error(`rfq.${action.kind}_rfq returned no RFQ name`)
      rfqs.push({
        id: name,
        // The supplier we SENT, not `w.supplier` echoed back. The caller keys its
        // supplier → RFQ-id map with `EnquiryItem.suppliers[]` values, and an echo that came
        // back display-shaped (`" Top Go FZE"`, leading space) would key a map nothing can find
        // — the RFQ would exist and the send would report "no RFQ was generated for it".
        supplier: action.supplier,
        item_count: items.length,
        // Neither write returns one; the workbook is `send_rfq`'s to produce. Blank, not guessed.
        excel_url: '',
        email_to: '',
        // `RfqStatus` has no `Draft` slot, so a fresh draft reads `'sent'` here (`toRfqStatus`'s
        // own documented lossiness). Nothing in the send flow reads this field — it takes `.id`
        // and `.supplier` — and `sent_at: null` below is the honest signal either way.
        status: toRfqStatus(w.status),
        sent_at: null,
        items: undefined,
      })
    } catch (error) {
      // Named and carried, never swallowed: the next supplier still gets their RFQ.
      failed.push({ supplier: action.supplier, message: error instanceof Error ? error.message : String(error) })
    }
  }

  if (rfqs.length === 0) return { rfqs, enquiry_status: null, skipped, failed }

  try {
    await callPut('enquiry', 'update_enquiry', {
      name: enquiryId,
      data: buildUpdateEnquiryWireBody({ status: 'RFQ Sent' }),
    })
    // No re-read: we know what we set, and `useEnquiryMutation` invalidates the enquiry query, so
    // the screen refetches the server's own truth regardless of what this returns.
    return { rfqs, enquiry_status: 'RFQ Sent', skipped, failed }
  } catch (error) {
    // The RFQs exist. Reporting the whole call as failed would send the purchaser back to press
    // the button again for RFQs they already have.
    const reason = error instanceof Error ? error.message : String(error)
    return {
      rfqs,
      enquiry_status: null,
      skipped,
      failed,
      status_error: `${rfqs.length} RFQ(s) were raised, but ${enquiryId} could not be moved to RFQ Sent: ${reason}`,
    }
  }
}

/**
 * Read-plan-write is a read-modify-write like any other on this enquiry, so it runs through the
 * SAME per-enquiry promise chain the item writes use (see the module header's "the read-modify-write
 * race"). Without it, two runs started close together **in this tab** — a double click, a retry
 * pressed over a slow first attempt — both read "this supplier has no RFQ", both `create_rfq`, and
 * the supplier is mailed twice. That is the identical outcome `buildRfqPlan` exists to prevent,
 * arriving through concurrency instead of through a stale list, and a disabled button is a UI
 * convention, not a guarantee.
 *
 * **What it does NOT cover — accepted residual risk.** `enquiryLocks` is a module-scoped `Map` in
 * ONE JavaScript context. A second browser tab (or window, or a second signed-in device) loads its
 * own module instance with its own empty map, so two tabs on the same enquiry can still both read
 * "no RFQ exists" and both create one — a supplier mailed twice. Nothing client-side can close
 * that: there is no shared lock across tabs that a server write would honour, and browser storage
 * is barred here anyway. The only real fix is server-side uniqueness on `(enquiry, supplier)` for
 * RFQ, which is filed as an ask in `docs/bosun-frappe-asks.md`. Until it lands, this lock covers
 * the common case (one purchaser, one tab, an impatient click) and the two-tab case is known and
 * accepted.
 *
 * Sharing the lock with `updateEnquiryItems` also means a line edit cannot land halfway through a
 * generate run and have its supplier assignment silently miss the RFQ it was meant for.
 */
function generateRfqs(enquiryId: string, lines: EnquiryItem[]): Promise<GenerateRfqsResult> {
  return withEnquiryLock(enquiryId, () => runRfqGeneration(enquiryId, lines))
}

/**
 * Same name, same cache invalidations as the `generate_rfqs` version it replaces — but it now
 * needs the enquiry's `lines`, because the grouping that used to happen server-side happens here.
 * Pass the same `lines` the screen is showing; the wire's own copy is not re-read, so an
 * unsaved triage edit is not silently generated from.
 */
export function useGenerateRfqs(
  enquiryId: string,
  lines: EnquiryItem[],
  options?: Options<GenerateRfqsResult, void>,
) {
  const queryClient = useQueryClient()
  return useEnquiryMutation(
    enquiryId,
    () => generateRfqs(enquiryId, lines),
    {
      ...options,
      onSuccess: (data, vars) => {
        // The board's status column changes too, so the list has to be refetched.
        void queryClient.invalidateQueries({ queryKey: ['enquiries'] })
        void queryClient.invalidateQueries({ queryKey: qk.rfqs(enquiryId) })
        options?.onSuccess?.(data, vars)
      },
    },
  )
}

/**
 * `rfq.send_rfq` — the only call that actually mails a supplier. The wire body is EXACTLY
 * `{rfq, to, cc, subject, note}`: the server appends the item table (read from the RFQ's own
 * snapshot) and the workbook link itself, so `note` must stay covering text only — see
 * `SendRfqPayload`'s own comment for why that split is deliberate, not an omission.
 *
 * Refuses to call the server at all when `to` is empty, rather than sending mail nobody
 * receives. A supplier with no resolvable address is a fact to REPORT to the purchaser, not a
 * silent no-op — this is the closing half of defect B3 (`Supplier.primary_email` is always `''`
 * live; the real address comes from `items.get_supplier_emails`, see `useItems.ts`'s
 * `useSupplierEmails`, which the caller must resolve `to`/`cc` through before calling this).
 */


/** `type` distinguishes a first send from a chase — kept on the signature for the toasts. */
type SendKind = 'rfq' | 'reminder'

/** What `rfq.send_rfq` answers — measured 2026-09-15: `{name, sent_at, sent_to: [...]}`. */
interface WireSendRfqResult {
  name?: string
  sent_at?: string | null
  sent_to?: string[] | string | null
}

/**
 * Our note without its greeting lines — `send_rfq` opens the mail with "Dear <supplier>,"
 * itself, and a note that also starts "Dear Sirs," produced "Dear Akhil, Dear Sirs," (measured).
 * `lib/rfq.ts`'s `withoutGreetings` — the supplier's name is not known here, so only "Dear …"
 * lines go; the template's bare "<name>," is removed where the note is built (`RfqPage`).
 */
export const withoutGreeting = (note: string): string => withoutGreetings(note)

/**
 * Mails an RFQ, or a chase for one, to its supplier — through `rfq.send_rfq` (D5).
 *
 * ---- Why `send_rfq`, as of 2026-09-15 ----
 *
 * It was dead for a month (`B3`), so sends went through `rfq_email_with_excel.send_rfq_email_
 * with_excel` — one recipient per call, no cc, the purchaser's copy folded into To. Re-measured
 * on RFQ-2026-00029: `send_rfq` mails, attaches the workbook the server builds from the RFQ's
 * rows, takes `to[]` and `cc[]` as REAL headers, composes the `[REQ-RFQ-…]` subject when none is
 * sent (the tag that links a reply back — `subject` is therefore never sent), stamps `sent_at`
 * and returns `sent_to`. Akhil chose it for the cc. What it does not do is honour `sender`
 * (`AKHIL Nair <…>` on the old path; `rfqai@lynx.sa` here) — filed.
 *
 * ---- A chase is the same call ----
 *
 * `send_rfq` has no reminder type; a chase re-attaches the same workbook and re-stamps
 * `sent_at`, which `buildRfqPlan` reads as "already sent" — correct for a chase. `rfq.remind_rfq`
 * is still not used: it answers `{sent: true, sent_to: null}` and records nothing.
 *
 * ---- One call, one outcome ----
 *
 * Every address goes in ONE request, so a send either reaches the server's whole list or throws
 * with nothing stamped — the partial-failure bookkeeping the per-recipient loop needed is gone.
 */
async function mailRfq(payload: SendRfqPayload, kind: SendKind): Promise<SendRfqResult> {
  if (payload.to.length === 0) {
    throw new Error(`No resolvable email address for ${payload.rfq} — not sent.`)
  }
  /*
    A CHASE reads the RFQ FIRST (Akhil, 2026-09-16: "make sure we are not updating the RFQ send
    time — those are important"). `send_rfq` overwrites `sent_at` with the chase's time, so the
    original is captured from the server before the call — never from a cached row — and put
    back the moment the mail has gone (`stampChase`). The rows come from the same read, because
    `update_rfq` replaces `items` wholesale.
  */
  const before = kind === 'reminder' && payload.chase ? await readRfqForChase(payload.rfq) : null
  let w: WireSendRfqResult
  try {
    w = await callPost<WireSendRfqResult>('rfq', 'send_rfq', {
      rfq: payload.rfq,
      to: payload.to,
      cc: payload.cc,
      /*
        A CHASE sends its subject; a first send does not. `send_rfq` honours `subject` when given
        (measured 2026-09-16 on RFQ-2026-00081: the Communication carried ours verbatim) and
        composes "Request for Quotation — <rfq> [REQ-<rfq>]" otherwise. The first send keeps the
        server's — that `[REQ-…]` tag threads the reply. The chase used to send none, so it went
        out word-for-word as the first mail, and the subject the purchaser edited in the Remind
        dialog went nowhere (Akhil, 2026-09-16). `buildReminderDraft` keeps the same tag.
      */
      ...(kind === 'reminder' && payload.subject.trim() !== '' ? { subject: payload.subject.trim() } : {}),
      // Plain text everywhere it is composed and edited; the server wraps `note` in `<p>` VERBATIM
      // (a newline is not a break — measured), so it is converted here, minus our greeting.
      note: textToHtml(withoutGreeting(payload.note)),
    })
  } catch (error) {
    throw new Error(`${payload.rfq} was not sent — ${error instanceof Error ? error.message : String(error)}`)
  }
  const sentTo = Array.isArray(w?.sent_to) ? w.sent_to : typeof w?.sent_to === 'string' && w.sent_to ? [w.sent_to] : []
  if (sentTo.length === 0) {
    throw new Error(`${payload.rfq} was not sent — the server confirmed no recipient`)
  }
  const result: SendRfqResult = { id: payload.rfq, sent_at: w.sent_at ?? null, sent_to: sentTo, failed: [] }
  if (kind === 'reminder' && payload.chase) {
    /*
      Record the chase AS a chase (Akhil, 2026-09-16). `send_rfq` has no reminder type: it
      re-stamped `sent_at`, so "sent 3 days ago" read "sent just now" after every chase and the
      chase itself was recorded nowhere — the purchaser had no way to see it had gone. `update_rfq`
      writes both timestamps (probed on RFQ-2026-00080): the original `sent_at` goes back, the
      server's time for the mail becomes `reminder_sent_at`. Best effort, after the mail: a stamp
      that fails must not read as a chase that did not go.
    */
    try {
      result.reminded_at = await stampChase(payload.rfq, before, payload.chase.original_sent_at, w.sent_at ?? null)
    } catch (error) {
      result.stamp_warning = `Reminder sent, but ${payload.rfq} could not be marked as chased (${error instanceof Error ? error.message : String(error)}).`
    }
  }
  return result
}

/** Frappe's own bookkeeping on a child row — never sent back on a wholesale `items` write. */
const CHILD_META = new Set(['name', 'idx', 'parent', 'parenttype', 'parentfield', 'docstatus', 'owner', 'creation', 'modified', 'modified_by', 'doctype'])

/**
 * Puts `sent_at` back and stamps `reminder_sent_at`. `update_rfq` replaces `items` WHOLESALE, so
 * the RFQ's rows are read from the document itself — `get_rfqs` carries no `description`, and
 * resending rows without it would wipe the purchaser's edited descriptions (D37's whole point).
 */
interface RfqBeforeChase {
  sent_at?: string | null
  items?: Array<Record<string, unknown>>
}

/** The RFQ as it stands BEFORE a chase — its real `sent_at` and its rows. */
const readRfqForChase = (rfqId: string): Promise<RfqBeforeChase> => getResourceDoc<RfqBeforeChase>('RFQ', rfqId)

async function stampChase(
  rfqId: string,
  before: RfqBeforeChase | null,
  /** The page's own copy — a fallback only, for when the pre-send read did not happen. */
  fallbackSentAt: string | null,
  remindedAt: string | null,
): Promise<string | null> {
  const doc = before ?? (await readRfqForChase(rfqId))
  const items = (doc.items ?? []).map((row) => Object.fromEntries(Object.entries(row).filter(([k]) => !CHILD_META.has(k))))
  // The server's own pre-chase value wins; the page's copy only when there was no read.
  const originalSentAt = (before ? before.sent_at : null) ?? fallbackSentAt ?? doc.sent_at ?? null
  await callPost('rfq', 'update_rfq', {
    name: rfqId,
    data: { sent_at: originalSentAt, reminder_sent_at: remindedAt, items },
  })
  return remindedAt
}

/** Exported for the test that pins the wire body to `{rfq, to, cc, note}` — no lines, ever. */
export const sendRfq = (payload: SendRfqPayload): Promise<SendRfqResult> => mailRfq(payload, 'rfq')

/** A chase for a supplier who hasn't answered. Same endpoint, `type: 'reminder'`. */
const sendRfqReminder = (payload: SendRfqPayload): Promise<SendRfqResult> => mailRfq(payload, 'reminder')

export function useSendRfq(enquiryId: string, options?: Options<SendRfqResult, SendRfqPayload>) {
  const queryClient = useQueryClient()
  return useEnquiryMutation(enquiryId, sendRfq, {
    ...options,
    onSuccess: (data, vars) => {
      // sent_at/status on the RFQ list changed; the mail went out from the supplier mailbox.
      void queryClient.invalidateQueries({ queryKey: qk.rfqs(enquiryId) })
      void queryClient.invalidateQueries({ queryKey: ['mail'] })
      // The ENQUIRY's own status moves too — the server sets `RFQ Sent` when the mail goes out.
      // `useEnquiryMutation` refreshes the single enquiry, but the Requirements board reads the
      // LIST, and without this it kept showing the pre-send status until something else happened
      // to refetch. The one screen meant to say what needs attention was the one going stale.
      void queryClient.invalidateQueries({ queryKey: ['enquiries'] })
      options?.onSuccess?.(data, vars)
    },
  })
}

/**
 * Chases a supplier who hasn't answered — the SAME endpoint as the first send, with
 * `type: 'reminder'`.
 *
 * ---- Why not `rfq.remind_rfq` ----
 *
 * Because it does nothing. Measured live 2026-08-07:
 *
 * ```
 * POST rfq.remind_rfq {"rfq_id":"RFQ-2026-00004"}
 *   -> {"sent": true, "rfq": "RFQ-2026-00004", "sent_to": null}
 * ```
 *
 * `sent: true` with `sent_to: null` — it resolved no recipient, wrote no timestamp
 * (`reminded_at` stayed `null`) and created no `Communication`. A Remind button wired to that
 * reports success and mails nobody, which is worse than one that fails: the purchaser believes
 * the supplier has been chased.
 *
 * `type: 'reminder'` on `send_rfq_email_with_excel` genuinely sends, re-attaches the SAME
 * workbook (so a chase cannot drift from what the supplier was originally asked to price), and
 * stamps `reminder_sent_at` on the RFQ — all three verified against the live bench.
 *
 * Takes the full `SendRfqPayload` rather than a bare id because the purchaser reviews and edits
 * the covering note before it goes; the RFQ screen composes the draft.
 */
export function useSendRfqReminder(enquiryId: string, options?: Options<SendRfqResult, SendRfqPayload>) {
  const queryClient = useQueryClient()
  return useEnquiryMutation(enquiryId, sendRfqReminder, {
    ...options,
    onSuccess: (data, vars) => {
      // `reminder_sent_at` on the RFQ list changed, and the chase is outgoing mail. The enquiry's
      // own status can move too, so the board is refreshed for the same reason `useSendRfq` gives.
      void queryClient.invalidateQueries({ queryKey: qk.rfqs(enquiryId) })
      void queryClient.invalidateQueries({ queryKey: ['mail'] })
      void queryClient.invalidateQueries({ queryKey: ['enquiries'] })
      options?.onSuccess?.(data, vars)
    },
  })
}

/* ---------- test-only export ---------- */

/** Exposed for `useEnquiries.test.ts`/`useRfqs.test.ts` — the wholesale-replace and race-safety
 * assertions, and the RFQ send/remind payload shapes, are exercised directly rather than through
 * a hook, matching this repo's no-`renderHook` test style. */
export const __test__ = {
  updateEnquiryItems,
  appendedRows,
  applyItemPatches,
  toWireItemWrite,
  withEnquiryLock,
  sendRfq,
  sendRfqReminder,
  toGeneratedRfq,
  toNewItemWireRow,
  // The client-side RFQ stopgap. `generateRfqs` is the whole decision — read fresh, plan, write
  // sequentially, report — so it is exercised directly against a stubbed `fetch` rather than
  // through a hook (this repo's vitest runs in a node environment; there is no DOM to render in).
  generateRfqs,
}
